import React from 'react';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import { Workstation } from '../models';
import AssociateNameRow from './AssociateNameRow';

export interface WorkstationCardProps {
  workstation: Workstation;
  /**
   * Fires when the user activates the per-row remove control on an
   * assigned associate.
   *
   * Optional: not used when `readOnly` is `true`, since the per-row
   * remove control is suppressed in that mode and there is no caller
   * for it.
   */
  onAssociateRemove?: (associateId: string, workstationId: string) => void;
  /**
   * Fires when the user clicks the "Add associate" footer button.
   *
   * Optional: not used when `readOnly` is `true`, since the "Add
   * associate" footer button is suppressed in that mode.
   */
  onAddAssociate?: (workstationId: string) => void;
  /** Set of currently selected associate IDs (for swap selection) */
  selectedAssociateIds?: Set<string>;
  /** Callback when a checkbox is toggled on an associate row */
  onToggleSelect?: (associateId: string) => void;
  /**
   * When `true`, the workstation card renders in informational mode for
   * the Published Assignment Screen: the "Add associate" footer button
   * is not rendered, and every assigned-associate row is rendered with
   * `showCheckbox={false}` so no per-row interactive control appears.
   * The workstation name, capacity, assigned-associate names, avatars,
   * warning indicator, and assignment-checkmark icon all render exactly
   * as in editable mode.
   *
   * Defaults to `false`, in which case behavior is bit-for-bit identical
   * to the editable Assignments page.
   */
  readOnly?: boolean;
}

/**
 * Card representing a single workstation within a direct process path
 * (e.g. "Primary line 1"). Lists every associate currently assigned to the
 * workstation using the shared AssociateNameRow component with a disabled
 * checkbox and a checkmark icon indicating assignment status.
 *
 * An "Add associate" button at the bottom invokes `onAddAssociate` to let
 * the user pick from the sidebar.
 *
 * Pure presentational: all state (assigned list membership) lives on the
 * parent and is passed in via the `workstation` prop.
 *
 * When `readOnly` is `true` (Published Assignment Screen): the "Add
 * associate" footer button is not rendered and per-row checkboxes are
 * suppressed. All data (workstation name, capacity, assigned-associate
 * names, avatars, warning indicator, assignment-checkmark icon) renders
 * exactly as in editable mode.
 */
const WorkstationCard: React.FC<WorkstationCardProps> = ({
  workstation,
  onAddAssociate,
  selectedAssociateIds,
  onToggleSelect,
  readOnly = false,
}) => {
  const handleAddAssociateClick = () => {
    if (onAddAssociate) {
      onAddAssociate(workstation.workstationId);
    }
  };

  return (
    <Box className="assignments-workstation-card" spacingInset="300">
      <Box className="assignments-workstation-card__header">
        <Text type="h100">{workstation.workstationName}</Text>
      </Box>

      <Box className="assignments-workstation-card__associates">
        {workstation.assignedAssociates.map(associate => (
          <AssociateNameRow
            key={associate.associateId}
            associateName={associate.associateName}
            associateAlias={associate.associateAlias}
            showCheckbox={!readOnly}
            checkboxChecked={selectedAssociateIds?.has(associate.associateId) ?? false}
            onCheckboxChange={() => onToggleSelect?.(associate.associateId)}
            showCheckmark={true}
            hasWarning={associate.hasWarning}
            data-testid={`workstation-associate-${associate.associateId}`}
          />
        ))}
      </Box>

      {!readOnly && (
        <Box className="assignments-workstation-card__footer">
          <button
            type="button"
            className="assignments-workstation-card__add-associate"
            onClick={handleAddAssociateClick}
          >
            {/* Meridian plus-circle keeps the icon optically centered and
                matches the small "+" disc shown in the mock. The accessible
                name stays "Add associate" so existing role/name queries (and
                screen readers) keep working. */}
            <span
              className="assignments-workstation-card__add-associate-icon"
              aria-hidden="true"
            >
              <Icon tokens={plusCircleTokens} />
            </span>
            Add associate
          </button>
        </Box>
      )}
    </Box>
  );
};

export default WorkstationCard;
